import { evaluate } from '../lib/flip';
import { daysBetween, pounds, today } from '../lib/format';
import { STATUSES, STATUS_LABEL, type Car, type Status } from '../lib/types';
import { useStore } from '../state/store';
import { go } from '../state/useHashRoute';
import { Checklist } from './Checklist';
import { DealForm } from './DealForm';
import { ResultsPanel } from './ResultsPanel';

function PlanVsNow({ car }: { car: Car }) {
  const plan = evaluate(car.planned);
  const now = evaluate(car.deal);
  const diff = now.profit - plan.profit;
  const rows: [string, string, string][] = [
    ['Cash invested', pounds(plan.cashInvested), pounds(now.cashInvested)],
    ['Total cost', pounds(plan.totalCosts), pounds(now.totalCosts)],
    ['Sale price', pounds(car.planned.salePrice), pounds(car.deal.salePrice)],
    ['Profit', pounds(plan.profit), pounds(now.profit)],
  ];
  return (
    <section className="card" aria-labelledby="pvn-h">
      <h2 id="pvn-h">Plan versus reality</h2>
      <table className="scenarios">
        <thead><tr><th scope="col">&nbsp;</th><th scope="col">Planned</th><th scope="col">Now</th></tr></thead>
        <tbody>{rows.map(([a, b, c]) => <tr key={a}><th scope="row">{a}</th><td>{b}</td><td>{c}</td></tr>)}</tbody>
      </table>
      <p className={diff < 0 ? 'neg' : diff > 0 ? 'pos' : 'muted'}>
        {diff === 0 ? 'Exactly as planned so far.' : `${diff > 0 ? 'Ahead of' : 'Behind'} plan by ${pounds(Math.abs(diff))}.`}
      </p>
      <p className="muted small">Update the numbers below as the real prices come in. The planned figures are kept so you can see where your estimates were off.</p>
    </section>
  );
}

export function CarPage({ id }: { id: string }) {
  const { data, updateCar, deleteCar } = useStore();
  const car = data.cars.find((c) => c.id === id);
  if (!car) {
    return (
      <div className="page">
        <h1>Car not found</h1>
        <p>It may have been deleted. <a href="#/pipeline">Back to my cars</a></p>
      </div>
    );
  }
  const held = daysBetween(car.boughtOn, car.soldOn);
  const setStatus = (status: Status) => {
    const patch: Partial<Car> = { status };
    if (status === 'bought' && !car.boughtOn) patch.boughtOn = today();
    if (status === 'sold' && !car.soldOn) patch.soldOn = today();
    updateCar(car.id, patch);
  };

  return (
    <div className="page two-col">
      <div>
        <p><a href="#/pipeline">← My cars</a></p>
        <h1>{car.title}</h1>
        <section className="card">
          <div className="grid">
            <div className="field"><label htmlFor="c-title">Name</label><input id="c-title" type="text" value={car.title} onChange={(e) => updateCar(car.id, { title: e.target.value })} /></div>
            <div className="field"><label htmlFor="c-reg">Registration</label><input id="c-reg" type="text" value={car.reg} onChange={(e) => updateCar(car.id, { reg: e.target.value.toUpperCase() })} /></div>
            <div className="field"><label htmlFor="c-src">Where from</label><input id="c-src" type="text" value={car.source} placeholder="Auction, private, garage" onChange={(e) => updateCar(car.id, { source: e.target.value })} /></div>
            <div className="field">
              <label htmlFor="c-status">Status</label>
              <select id="c-status" value={car.status} onChange={(e) => setStatus(e.target.value as Status)}>
                {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
              </select>
            </div>
            <div className="field"><label htmlFor="c-bought">Bought on</label><input id="c-bought" type="date" value={car.boughtOn} onChange={(e) => updateCar(car.id, { boughtOn: e.target.value })} /></div>
            <div className="field"><label htmlFor="c-sold">Sold on</label><input id="c-sold" type="date" value={car.soldOn} onChange={(e) => updateCar(car.id, { soldOn: e.target.value })} /></div>
          </div>
          {held !== null && <p className="muted">Held for {held} day{held === 1 ? '' : 's'}.</p>}
          <div className="field">
            <label htmlFor="c-notes">Notes</label>
            <textarea id="c-notes" rows={3} value={car.notes} onChange={(e) => updateCar(car.id, { notes: e.target.value })} />
          </div>
        </section>

        <PlanVsNow car={car} />
        <DealForm deal={car.deal} onChange={(deal) => updateCar(car.id, { deal })} />
        <Checklist checks={car.checks} onToggle={(cid, on) => updateCar(car.id, { checks: { ...car.checks, [cid]: on } })} />
        <div className="actions">
          <button type="button" className="btn danger" onClick={() => { if (window.confirm(`Delete "${car.title}"? This cannot be undone.`)) { deleteCar(car.id); go('/pipeline'); } }}>Delete this car</button>
        </div>
      </div>
      <ResultsPanel deal={car.deal} settings={data.settings} />
    </div>
  );
}
